// PROD-957 Part A: which visit the patient's case header shows.
//
// API PROD-957 freezes the finalize envelope (cases.after_visit_summary {finalizedAt,
// finalizedBy, examinationNotes, consultNote, ...}) at encounter 1 for the life of the case.
// The store read the header from that envelope first, so after a follow-up the patient would
// see the FIRST visit's finalize date, Date of Service, provider, signature line, exam and AVS
// date. This module reads the header from the latest SIGNED visit instead: the highest-numbered
// encounter row with `finalized === true` and a consult note. A sealed row (finalized, no
// note: a terminal "complete" cassette) is not a signed visit.
//
// Legacy cases (no encounter rows, or none signed) keep today's chain exactly.
//
// PROD-1077: a visit whose note the physician HOLDS (utils/noteHold.js) is still a signed visit
// (its date and signer head the page), but its note is never the case's note: the note is the
// latest signed visit that is NOT held, else ''.
//
// PROD-1079: md autosaves the UNSIGNED draft note and exam into the case-level copies
// (cases.consult_note / examination_notes, diagnose_data.consultNote ...) three seconds after the
// physician types, and the API sends them to the patient. So a note or exam reaches a patient
// screen, the records PDF or the chat agent only from a signed, not-held encounter row; the
// case-level copies are read only for a legacy case (no signed row) that shows a finalize.
//
// PROD-1077 fix 3 (train review M1): an encounter-1 row that is finalized and not held but
// carries NO note on the row (API migrate_encounters from a null case.consult_note, or the
// legacy seed in acknowledge_encounter) is a signed visit, and its note / exam / diagnosis are
// the case-level (envelope) copies, which are frozen at that signature. This is the API's
// narrow exception (_note_release_record enc1_open): those encounter-1 slots are kept, unless
// their text is a held or unsigned row's note. Only encounter 1; a sealed later row (finalized,
// no note) is still not a signed visit.
//
// PROD-957 Part B: "My medical note" > Billing Codes > Diagnosis lists the diagnoses of the visit
// the header shows (shownDiagnosisTexts below): a signed visit V >= 2 gives the texts of its own
// `diagnoses`; V = 1 or no V keeps today's billing-code texts.
//
// ★ TWO IMPORTS, ./noteHold.js and ./diagnosisText.js (both import-free). scripts/check-signed-visit-header.mjs and
//   check-note-hold-per-encounter.mjs load this file in node (inlining that import) and run the
//   rules on synthetic cases. It makes no request and logs nothing: the values are clinical.

import { isEncounterNoteHeld } from './noteHold.js'
import { cleanDiagnosisText, diagnosisTextsOf } from './diagnosisText.js'

const num = (e) => {
  const n = e?.encounter_number ?? e?.encounterNumber
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

const str = (v) => (typeof v === 'string' ? v.trim() : '')

/** The consult note text an encounter row carries, or ''. */
export function visitNote(enc) {
  return str(enc?.consult_note) || str(enc?.consultNote)
}

/** The exam text of an encounter row: a string, or {notes} / {text}; anything else ''. */
export function visitExamText(enc) {
  const v = enc?.exam_notes ?? enc?.examNotes
  if (typeof v === 'string') return v.trim()
  if (v && typeof v === 'object') return str(v.notes) || str(v.text)
  return ''
}

/** The finalize date of an encounter row, or null. */
export function visitDate(enc) {
  return enc?.finalized_at || enc?.finalizedAt || null
}

const rowsOf = (c) => (Array.isArray(c?.encounters) ? c.encounters.filter((e) => e && typeof e === 'object') : [])

/** PROD-1077 fix 3: encounter 1 finalized, not held, and no note on its row (the API's
 *  enc1_open). Its note is the case-level encounter-1 copy (enc1SlotNote). */
export function isEnc1Open(enc) {
  return enc?.finalized === true && num(enc) === 1 && !isEncounterNoteHeld(enc) && !visitNote(enc)
}

/** A signed visit: the row is finalized and carries its consult note, or its note is held
 *  (the API blanks a held note, so a held row has no text), or it is encounter 1 signed
 *  without a row note (isEnc1Open). */
export function isSignedVisit(enc) {
  return enc?.finalized === true && (!!visitNote(enc) || isEncounterNoteHeld(enc) || isEnc1Open(enc))
}

/** A released visit: signed, not held, with its note (or encounter 1 open, whose note is the
 *  case-level copy). Only these notes reach the patient. */
export function isReleasedVisit(enc) {
  return enc?.finalized === true && !isEncounterNoteHeld(enc) && (!!visitNote(enc) || isEnc1Open(enc))
}

/** PROD-1077 fix 3: a case-level encounter-1 note text, '' when it equals a held or unsigned
 *  row's note (the API's withheld set: an old API still sends those rows' text). */
function enc1SlotNote(c, text) {
  const t = str(text)
  if (!t) return ''
  const withheld = rowsOf(c).some((e) => (e.finalized !== true || isEncounterNoteHeld(e)) && visitNote(e) === t)
  return withheld ? '' : t
}

/** The note a released visit R gives the patient: its row note, or for encounter 1 open the
 *  case-level copy `enc1Text`; '' without R. */
function releasedNoteOf(c, R, enc1Text) {
  if (!R) return ''
  return visitNote(R) || (isEnc1Open(R) ? enc1SlotNote(c, enc1Text) : '')
}

/** PROD-957 Part B: the diagnosis texts of an encounter row's `diagnoses` (a string item, or an
 *  object item's description / diagnosis / name), raw ICD-10 codes cut. [] when the row has none. */
export function visitDiagnosisTexts(enc) {
  const list = Array.isArray(enc?.diagnoses) ? enc.diagnoses : []
  return list
    .map((d) => cleanDiagnosisText(d && typeof d === 'object' ? (d.description || d.diagnosis || d.name) : d))
    .filter(Boolean)
}

/** The latest signed visit of a case (highest encounter number), else null. */
export function signedVisitOf(c) {
  let best = null
  for (const e of rowsOf(c)) {
    if (!isSignedVisit(e)) continue
    if (best == null || (num(e) ?? 0) > (num(best) ?? 0)) best = e
  }
  return best
}

/** The latest released visit of a case (signed, not held), else null. */
export function releasedVisitOf(c) {
  let best = null
  for (const e of rowsOf(c)) {
    if (!isReleasedVisit(e)) continue
    if (best == null || (num(e) ?? 0) > (num(best) ?? 0)) best = e
  }
  return best
}

/** The case statuses only a signature writes: the API's _MIGRATE_SIGNED_CASE_STATUSES. */
export const SIGNED_CASE_STATUSES = ['completed', 'in_follow_up', 'in_observation']

/**
 * PROD-1079: a LEGACY case that shows a finalize (its case-level copies are then the signed
 * note). The API's legacy rule (_avs_legacy_released): NO encounter rows at all, and a signed
 * case status or a finalize stamp. A case WITH rows is signed only through a signed row
 * (signedVisitOf), whatever its status says: a `completed` case whose rows are all unsigned
 * carries md's draft in the case-level copies.
 */
export function caseShowsFinalize(c, finData) {
  if (rowsOf(c).length > 0) return false
  return !!(finData?.finalizedAt || c?.completed_at || c?.finalizedAt || SIGNED_CASE_STATUSES.includes(c?.status))
}

const listOrNull = (v) => (Array.isArray(v) ? (v.length ? v : null) : (v || null))

/**
 * PROD-1079 (extension): the diagnosis and treatment plan the patient's case carries, from the
 * raw API case `c` and its parsed envelope `finData`. md autosaves its working choices into
 * diagnose_data.selectedDiagnoses / selectedTreatments every few seconds, and the store used
 * them as the fallback, so the chat agent heard a diagnosis the physician had not signed (and
 * might un-tick). Now:
 *   - rows exist: only SIGNED visits (isSignedVisit): the latest signed visit that HAS
 *     diagnoses (resp. treatments), so a follow-up signed without diagnoses keeps the earlier
 *     visit's; else the case columns diagnosis / treatment_plan (written at signing);
 *     no signed row -> null, whatever the draft arrays hold;
 *   - no rows (legacy, or encounter 1 before its row exists): today's chain, but only once
 *     the case shows a finalize (caseShowsFinalize); before that the arrays are md's draft.
 */
export function signedDiagnosisOf(c, finData = null) {
  const apiDiagnose = (c && typeof c.diagnose === 'object' && c.diagnose) || {}
  if (rowsOf(c).length === 0) {
    if (!caseShowsFinalize(c, finData)) return { diagnosis: null, treatmentPlan: null }
    return {
      diagnosis: c?.diagnosis || apiDiagnose.selectedDiagnoses || apiDiagnose.diagnosis,
      treatmentPlan: c?.treatment_plan || apiDiagnose.selectedTreatments,
    }
  }
  const signed = rowsOf(c).filter(isSignedVisit).sort((a, b) => (num(b) ?? 0) - (num(a) ?? 0)) // latest first
  if (signed.length === 0) return { diagnosis: null, treatmentPlan: null }
  const latestWith = (key) => { for (const e of signed) { const v = listOrNull(e[key]); if (v) return v } return null }
  // fix 3: encounter 1 signed without a row note -> its selections are the case-level copies
  //   (the API keeps the encounter-1 draft keys once encounter 1 is finalized)
  const open = signed.some(isEnc1Open)
  return {
    diagnosis: latestWith('diagnoses') || c?.diagnosis || (open ? apiDiagnose.selectedDiagnoses || apiDiagnose.diagnosis : null) || null,
    treatmentPlan: latestWith('treatments') || c?.treatment_plan || (open ? apiDiagnose.selectedTreatments : null) || null,
  }
}

/**
 * PROD-1079: the note and exam the patient's chat agent is given, from the store's case: the
 * latest released visit's (signed, not held). A case whose signed visits are all held gives ''.
 * A legacy case (no signed row) gives the store's note / exam only once it shows a finalize.
 */
export function agentVisitContext(c) {
  const R = releasedVisitOf(c)
  if (R) {
    const V = signedVisitOf(c)
    const exam = visitExamText(R) || (R === V && num(R) === 1 ? str(c?.diagnose?.examinationNotes) : '')
    return { consultNote: releasedNoteOf(c, R, c?.diagnose?.consultNote), examinationNotes: exam }
  }
  if (signedVisitOf(c) || !caseShowsFinalize(c, null)) return { consultNote: '', examinationNotes: '' }
  return { consultNote: str(c?.diagnose?.consultNote), examinationNotes: str(c?.diagnose?.examinationNotes) }
}

/**
 * PROD-1077: the consult notes a records page prints for case `c` (get_case shape), one entry
 * per signed visit in visit order: {encounterNo, date, held, note}. A held visit carries no
 * text, whatever the row holds. Without a signed visit: the legacy case-level note (from the
 * caller) as one undated entry, only when the case shows a finalize (PROD-1079: else it is a
 * draft), or none.
 */
export function recordsVisitNotes(c, legacyNote, finData) {
  const signed = rowsOf(c).filter(isSignedVisit).sort((a, b) => (num(a) ?? 0) - (num(b) ?? 0))
  if (signed.length) {
    // fix 3: encounter 1 open prints the case-level note; with none it is not an entry
    return signed.map((e) => {
      const held = isEncounterNoteHeld(e)
      return { encounterNo: num(e), date: visitDate(e), held, note: held ? '' : releasedNoteOf(c, e, legacyNote) }
    }).filter((v) => v.held || v.note)
  }
  const note = caseShowsFinalize(c, finData) ? str(legacyNote) : ''
  return note ? [{ encounterNo: null, date: null, held: false, note }] : []
}

// cases.finalized_by is md's JSON.stringify({id, name, credentials}); parse it when it is a string.
const parsedSigner = (raw) => {
  if (typeof raw !== 'string') return raw
  try { return JSON.parse(raw) } catch { return raw }
}

/**
 * The header of the case the patient reads, from the raw API case `c` (get_cases shape) and
 * its parsed finalize envelope `finData` (or null), and the encounter the patient stands in
 * (`current`, normalised by the store: utils/caseEncounters.js names the store the one file
 * that may spell that fallback):
 *   {finalizedAt, finalizedBy, examinationNotes, consultNote, avsEncounterNo}
 * With a signed visit V: V's date, the signer of the latest signed visit (the case column
 * finalized_by, which API 957 does not freeze), V's exam (for V >= 2 never another visit's),
 * the note of the latest released visit (never a held one; '' when none), and the AVS of the current encounter when that row is a signed visit, else V's.
 * Without one: today's chain, byte for byte, except that the note and exam are '' until the
 * case shows a finalize (PROD-1079).
 */
export function visitHeaderOf(c, finData, current = null) {
  const apiDiagnose = (c && typeof c.diagnose === 'object' && c.diagnose) || {}
  const V = signedVisitOf(c)
  if (!V) {
    // PROD-1079: before a finalize the case-level note / exam are md's unsigned draft.
    const signed = caseShowsFinalize(c, finData)
    return {
      finalizedAt: finData?.finalizedAt || c?.completed_at,
      finalizedBy: finData?.finalizedBy || parsedSigner(c?.finalized_by),
      examinationNotes: signed ? (finData?.examinationNotes || c?.examination_notes || apiDiagnose.examinationNotes) : '',
      consultNote: signed ? (finData?.consultNote || c?.consult_note || apiDiagnose.consultNote) : '',
      avsEncounterNo: current,
    }
  }
  const vNo = num(V)
  const column = parsedSigner(c?.finalized_by)
  const columnNamed = column && typeof column === 'object' && str(column.name) ? column : null
  const exam = visitExamText(V)
  const currentRow = rowsOf(c).find((e) => num(e) === current)
  return {
    finalizedAt: visitDate(V) || c?.completed_at || finData?.finalizedAt,
    finalizedBy: columnNamed || finData?.finalizedBy || column,
    examinationNotes: vNo != null && vNo >= 2
      ? exam
      : (exam || finData?.examinationNotes || c?.examination_notes || apiDiagnose.examinationNotes),
    consultNote: releasedNoteOf(c, releasedVisitOf(c), finData?.consultNote || c?.consult_note || apiDiagnose.consultNote),
    avsEncounterNo: currentRow && isSignedVisit(currentRow) ? current : vNo,
  }
}

/**
 * PROD-957 Part B: the diagnosis texts "My medical note" lists, for the raw case `c` and the
 * envelope's billing codes. The visit the header shows is signedVisitOf(c): for V >= 2 its own
 * diagnoses (never encounter 1's; none when the row has none; none when V's note is held, since
 * a held visit's content never reaches the patient); for V = 1 or no V, today's billing-code texts.
 */
export function shownDiagnosisTexts(c, billingCodes) {
  const V = signedVisitOf(c)
  if (V && (num(V) ?? 0) >= 2) return isEncounterNoteHeld(V) ? [] : visitDiagnosisTexts(V)
  return diagnosisTextsOf(billingCodes)
}
